import React from 'react'
import { __TEST__ } from '@mote/helpers/constants'
import { DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { useFlightRecorder } from '@mote/presentation/src/recorder/state/useFlightRecorder'
import { getApiClientConfig } from '../apiClientConfig'
import { apiClient } from '../client'
import { FaultInjector } from '../faultInjector'

const logD = DiagnosticsLogger('useGenericRequestHandler', 'Transport')
const INITIAL_STATE = {
  data: null,
  loading: false,
  error: null,
  fetched: false
}

export const useGenericRequestHandler = ({
  route,
  method = 'GET',
  options = {},
  enabled = true,
  responseType = 'json',
  abortable = true,
  refetchInterval = 0
}) => {
  const [state, setState] = React.useState(() => {
    return {
      ...INITIAL_STATE,
      loading: method === 'GET' && enabled
    }
  })
  const mounted = React.useRef(false)
  const abortController = React.useRef(null)
  const refetchIntervalTimer = React.useRef(null)

  const { flightState, bumpRequestRetries } = useFlightRecorder()
  let routeConfig = getApiClientConfig(route)
  const retryLimit = routeConfig?.retry?.limit || 0

  routeConfig = {
    ...routeConfig,
    hooks: {
      beforeRetry: [
        ({ request, error }) => {
          const url = new URL(request.url)
          const parsedRoute = `${url.pathname.replace('/', '')}${url.search}`

          if (!flightState.requests[parsedRoute]) {
            logD.error(
              `[${parsedRoute}] Failed, no retry handling [${error?.response}] [${error}]`
            )
            return
          }

          if (flightState.requests[parsedRoute] < retryLimit) {
            logD.info(
              `[${parsedRoute}] Retry attempt #${
                flightState.requests[parsedRoute] + 1
              }`
            )
            bumpRequestRetries(parsedRoute)
          } else {
            logD.error(`[${parsedRoute}] Giving up on retries`)
            throw new apiClient.HTTPError(error?.response)
          }
        }
      ]
    }
  }
  const extendedApiClient = apiClient.extend(routeConfig)
  const initiateRequest = React.useCallback(
    async (dynamicOptions = {}, retry = retryLimit - 1) => {
      const defaultDynamicOptions = {
        body: null,
        variables: {},
        routeOverride: null,
        abortable: true
      }
      const customOptions = { ...defaultDynamicOptions, ...dynamicOptions }
      let error = state.error
      let data = state.data
      let newState = null

      if (mounted.current && !state.loading) {
        setState(prevState => ({
          ...prevState,
          loading: true
        }))
      }

      if (Object.keys(customOptions.variables).length) {
        options.json = { ...options.json, ...customOptions.variables }
      }

      if (!options.json && method === 'POST') {
        options.json = {}
      }

      if (customOptions.body) {
        delete options.json
        options.body = customOptions.body
      }

      if (customOptions.timeout) {
        delete options.timeout
        options.timeout = customOptions.timeout
      }

      if (abortController.current && abortable) {
        abortController.current.abort()
      }
      abortController.current = new AbortController()

      try {
        options.signal = abortController.current?.signal
        if (process.env.REACT_APP_FAULT_INJECTION) {
          data = await FaultInjector({
            route,
            customOptions,
            responseType,
            extendedApiClient
          })
        }
        let startDate = new Date()
        data = await extendedApiClient[method.toLowerCase()](
          customOptions.routeOverride ?? route,
          options
        )[responseType]()
        error = null
        let endDate = new Date()
        let elapsedMilliseconds = Math.floor(
          endDate.getTime() - startDate.getTime()
        )
        logD.info(
          `[${
            customOptions.routeOverride ?? route
          }] completed in ${elapsedMilliseconds} ms`
        )
      } catch (err) {
        console.error(err?.message)
        error = err
      }

      // Checking if timeout error and retry request if retry is remaining
      if (error && error.name === 'TimeoutError' && retry > 0) {
        const path = customOptions.routeOverride || route
        if (path.includes('m/addChunk') || path.includes('m/register')) {
          bumpRequestRetries(path)
        }
        const increasedTimeout =
          routeConfig.timeout + (retryLimit - retry) * 4000
        return await initiateRequest(
          { ...dynamicOptions, timeout: __TEST__ ? 200 : increasedTimeout },
          retry - 1
        )
      }

      if (error?.name === 'AbortError') {
        console.error('Fetch aborted')
      }

      newState = {
        loading: false,
        fetched: error?.name !== 'AbortError',
        error: error?.name === 'AbortError' ? null : error,
        data
      }
      abortController.current = null
      if (!mounted.current) return newState
      setState(newState)

      return newState
    },
    [state, extendedApiClient, options, method]
  )

  React.useEffect(() => {
    if (method !== 'GET' || (method === 'GET' && !enabled)) return

    initiateRequest()
  }, [enabled])

  React.useEffect(() => {
    mounted.current = true

    return () => {
      abortController.current?.abort()
      mounted.current = false
    }
  }, [])

  React.useEffect(() => {
    if (!refetchInterval || state.loading || !enabled) {
      clearInterval(refetchIntervalTimer.current)
      refetchIntervalTimer.current = null
      return
    }

    if (refetchIntervalTimer.current) return

    refetchIntervalTimer.current = setInterval(
      () => initiateRequest(),
      refetchInterval
    )

    return () =>
      refetchIntervalTimer.current &&
      clearInterval(refetchIntervalTimer.current)
  }, [enabled, refetchInterval, state, refetchIntervalTimer])

  const flush = React.useCallback(() => {
    setState(INITIAL_STATE)
    if (refetchIntervalTimer.current) {
      clearInterval(refetchIntervalTimer.current)
      refetchIntervalTimer.current = null
    }
  }, [])

  return {
    ...state,
    flush,
    cancel: abortController.current,
    mutate: method === 'GET' ? null : initiateRequest,
    refetch: method === 'GET' ? initiateRequest : null
  }
}
